import type {
  User, PublicUser, Chat, Message, Settings, ChatType,
} from "@glass/shared";

const TOKEN_KEY = "glass.token";

export const api = {
  origin(): string {
    return (import.meta.env.VITE_API_ORIGIN as string | undefined) || window.location.origin;
  },
  setToken(t: string | null) {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  },
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has("content-type") && init.body && typeof init.body === "string") {
      headers.set("content-type", "application/json");
    }
    const t = api.getToken();
    if (t) headers.set("authorization", `Bearer ${t}`);
    const res = await fetch(`${api.origin()}${path}`, { ...init, headers });
    if (!res.ok) {
      let detail = res.statusText;
      try {
        const j = await res.json() as { error?: string };
        if (j.error) detail = j.error;
      } catch { /* empty */ }
      throw new Error(`${res.status}: ${detail}`);
    }
    if (res.status === 204) return undefined as T;
    return await res.json() as T;
  },
  health(): Promise<{ ok: boolean; app: string; time: string }> {
    return api.request("/api/health");
  },
  config(): Promise<{
    google_client_id: string;
    turnstile_site_key: string;
    jitsi_domain: string;
    app_name: string;
    disappearing_min_seconds: number;
    disappearing_max_seconds: number;
  }> {
    return api.request("/api/config");
  },
  loginNative(id_token: string, turnstile?: string): Promise<{ token: string; user: User }> {
    return api.request("/api/auth/google/native", {
      method: "POST",
      body: JSON.stringify({ id_token, turnstile }),
    });
  },
  me(): Promise<{ user: User }> {
    return api.request("/api/auth/me");
  },
  listChats(): Promise<{ chats: Chat[] }> {
    return api.request("/api/chats");
  },
  createChat(body: {
    type: ChatType; title: string; description?: string; username?: string;
    is_public?: boolean; initial_members?: string[];
  }): Promise<{ chat: Chat }> {
    return api.request("/api/chats", { method: "POST", body: JSON.stringify(body) });
  },
  getChat(id: string): Promise<{ chat: Chat }> {
    return api.request(`/api/chats/${id}`);
  },
  loadMessages(id: string, before?: string): Promise<{ messages: Message[] }> {
    const u = new URL(`/api/chats/${id}/messages`, api.origin());
    if (before) u.searchParams.set("before", before);
    u.searchParams.set("limit", "50");
    return api.request(u.pathname + u.search);
  },
  pinChat(id: string, pinned: boolean) {
    return api.request<{ ok: true }>(`/api/chats/${id}/pin`, {
      method: "POST", body: JSON.stringify({ pinned }),
    });
  },
  archiveChat(id: string, archived: boolean) {
    return api.request<{ ok: true }>(`/api/chats/${id}/archive`, {
      method: "POST", body: JSON.stringify({ archived }),
    });
  },
  muteChat(id: string, muted: boolean) {
    return api.request<{ ok: true }>(`/api/chats/${id}/mute`, {
      method: "POST", body: JSON.stringify({ muted }),
    });
  },
  leaveChat(id: string) {
    return api.request<{ ok: true }>(`/api/chats/${id}/leave`, { method: "POST" });
  },
  clearHistory(id: string) {
    return api.request<{ ok: true }>(`/api/chats/${id}/clear-history`, { method: "POST" });
  },
  setWallpaper(id: string, wallpaper_url: string | null) {
    return api.request<{ ok: true }>(`/api/chats/${id}/wallpaper`, {
      method: "POST", body: JSON.stringify({ wallpaper_url }),
    });
  },
  setDisappearing(id: string, seconds: number | null) {
    return api.request<{ ok: true }>(`/api/chats/${id}/disappearing`, {
      method: "POST", body: JSON.stringify({ seconds }),
    });
  },
  search(q: string): Promise<{ users: PublicUser[]; chats: Chat[] }> {
    return api.request(`/api/users/search?q=${encodeURIComponent(q)}`);
  },
  uploadMedia(blob: Blob, contentType: string): Promise<{ key: string; url: string; size: number; content_type: string }> {
    return api.request("/api/media/upload", {
      method: "POST",
      headers: { "content-type": contentType },
      body: blob,
    });
  },
  jitsiRoom(chat_id: string, lobby?: boolean): Promise<{ domain: string; room: string; user: { id: string; name: string; email: string }; options: Record<string, boolean> }> {
    return api.request("/api/jitsi/room", {
      method: "POST", body: JSON.stringify({ chat_id, lobby }),
    });
  },
  startAudio(chat_id: string, title?: string): Promise<{ room_id: string }> {
    return api.request(`/api/chats/${chat_id}/audio/start`, {
      method: "POST", body: JSON.stringify({ title }),
    });
  },
  activeAudio(chat_id: string): Promise<{ room: { id: string; title: string; started_at: string } | null }> {
    return api.request(`/api/chats/${chat_id}/audio/active`);
  },
  getSettings(): Promise<{ settings: Settings }> {
    return api.request("/api/users/settings");
  },
  updateSettings(patch: Partial<Settings>): Promise<{ settings: Settings }> {
    return api.request("/api/users/settings", { method: "POST", body: JSON.stringify(patch) });
  },
  setUsername(username: string) {
    return api.request<{ ok: true }>("/api/users/me/username", {
      method: "POST", body: JSON.stringify({ username }),
    });
  },
  setPresence(online: boolean, stealth: boolean) {
    return api.request<{ ok: true }>("/api/users/me/presence", {
      method: "POST", body: JSON.stringify({ online, stealth }),
    });
  },
  resolveShare(token: string): Promise<{ kind: "user" | "chat"; target: Record<string, unknown> }> {
    return api.request(`/api/share/${encodeURIComponent(token)}`);
  },
  joinShare(token: string): Promise<{ ok: true; chat_id: string }> {
    return api.request("/api/share/join", { method: "POST", body: JSON.stringify({ token }) });
  },
  turnstileVerify(token: string, endpoint: string): Promise<{ ok: boolean; band: string; score: number }> {
    return api.request("/api/turnstile/verify", {
      method: "POST", body: JSON.stringify({ token, endpoint }),
    });
  },
};
